import { useRef, useState, useCallback, useEffect } from "react";
import { SplitReveal, FadeUp } from "../components/SplitReveal";
import { useLanguage } from "../lib/i18n";
import { IMAGES } from "../lib/images";

const VEHICLES = [IMAGES.heroSuv, IMAGES.hoodMacro, IMAGES.blackCoupe];

// ÖNCESİ / SONRASI — draggable vertical divider comparison, tabbed vehicles.
export function BeforeAfter() {
  const { t } = useLanguage();
  const [split, setSplit] = useState(50);
  const [tab, setTab] = useState(0);
  const ref = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setSplit(Math.max(2, Math.min(98, pct)));
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  return (
    <section className="relative bg-[#08090B] px-6 py-24 md:px-12 md:py-40" data-testid="before-after-section">
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-12 max-w-2xl">
          <FadeUp>
            <p className="heading mb-4 text-sm not-italic tracking-[0.3em] text-hars-orange">{t.beforeAfter.kicker}</p>
          </FadeUp>
          <SplitReveal text={t.beforeAfter.title} className="heading text-5xl text-white md:text-7xl" />
          <FadeUp delay={0.1}>
            <p className="mt-6 font-inter text-base text-hars-muted md:text-lg">{t.beforeAfter.text}</p>
          </FadeUp>
        </div>

        <div className="mb-6 flex gap-3">
          {t.beforeAfter.vehicles.map((v, i) => (
            <button
              key={i}
              onClick={() => setTab(i)}
              data-cursor
              data-testid={`ba-tab-${i}`}
              className={`rounded-full border px-5 py-2 font-inter text-sm font-medium transition-colors duration-300 ${
                tab === i
                  ? "border-hars-orange bg-hars-orange/10 text-hars-orange"
                  : "border-white/10 text-hars-muted hover:text-white"
              }`}
            >
              {v}
            </button>
          ))}
        </div>

        <div
          ref={ref}
          data-testid="before-after-stage"
          className="relative aspect-[16/9] w-full select-none overflow-hidden rounded-2xl border border-white/5"
        >
          {/* After (protected) base */}
          <img
            src={VEHICLES[tab]}
            alt="HARSPRO korumalı araç"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ filter: "brightness(1.1) contrast(1.15) saturate(1.15)" }}
          />
          <span
            className="heading absolute right-5 top-5 rounded-full bg-hars-orange px-4 py-1.5 text-xs not-italic tracking-widest text-white transition-opacity"
            style={{ opacity: split < 85 ? 1 : 0.2 }}
          >
            {t.beforeAfter.after}
          </span>

          {/* Before (unprotected) clipped layer */}
          <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - split}% 0 0)` }}>
            <img
              src={VEHICLES[tab]}
              alt="Korumasız yıpranmış araç"
              className="absolute inset-0 h-full w-full object-cover"
              style={{ filter: "grayscale(0.55) brightness(0.6) contrast(0.85)" }}
            />
            <div className="absolute inset-0 bg-black/25" />
            <span
              className="heading absolute left-5 top-5 rounded-full border border-white/30 bg-black/50 px-4 py-1.5 text-xs not-italic tracking-widest text-white transition-opacity"
              style={{ opacity: split > 15 ? 1 : 0.2 }}
            >
              {t.beforeAfter.before}
            </span>
          </div>

          {/* Divider handle */}
          <div
            className="absolute top-0 z-10 h-full w-[3px] -translate-x-1/2 bg-hars-orange"
            style={{ left: `${split}%` }}
          >
            <button
              aria-label="Karşılaştırma ayırıcısı"
              data-cursor
              data-testid="ba-handle"
              onMouseDown={() => (dragging.current = true)}
              onTouchStart={() => (dragging.current = true)}
              className="absolute top-1/2 left-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-grab items-center justify-center rounded-full bg-hars-orange text-white shadow-[0_0_25px_rgba(240,116,42,0.6)] active:cursor-grabbing"
            >
              <span className="text-lg leading-none">⟺</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
